// services/favorites.js
import { supabase } from '../config/supabase';

class FavoritesService {
  /**
   * Ottiene tutti i preferiti dell'utente loggato
   * @param {string} sortBy - 'date' o 'name' per ordinare
   */
  async getUserFavorites(sortBy = 'date') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Prima assicurati che l'utente esista in users
      await this.ensureUserExists(user.id);

      let query = supabase
        .from('artist_favorites')
        .select(`
          saved_at,
          artist:artists (
            id,
            spotify_id,
            deezer_id,
            deezer_url,
            name,
            image_url,
            spotify_url,
            genres
          )
        `)
        .eq('user_id', user.id);

      // Ordina nel database
      if (sortBy === 'name') {
        query = query.order('name', { ascending: true, foreignTable: 'artists' });
      } else {
        query = query.order('saved_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('[Favorites] Error loading favorites:', error);
        return [];
      }

      return data.map(fav => ({
        ...fav.artist,
        saved_at: fav.saved_at
      }));
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return [];
    }
  }

  /**
   * Verifica se un artista è nei preferiti dell'utente
   */
  async isFavorite(artistId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('artist_favorites')
        .select('artist_id')
        .eq('user_id', user.id)
        .eq('artist_id', artistId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
        console.error('[Favorites] Error checking favorite:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return false;
    }
  }

  /**
   * Ottiene lista di artist_id preferiti (per check veloce)
   */
  async getFavoriteArtistIds() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('artist_favorites')
        .select('artist_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('[Favorites] Error loading favorite IDs:', error);
        return [];
      }

      return data.map(fav => fav.artist_id);
    } catch (error) {
      console.error('[Favorites] Error:', error);
      return [];
    }
  }

  /**
   * Salva un artista nei preferiti.
   * Supporta sia Spotify (spotify_id) che Deezer (deezer_id).
   * In modalità Deezer, usa "deezer:{id}" come chiave univoca nel campo spotify_id.
   */
  async addFavorite(artistData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Assicurati che l'utente esista in users
      await this.ensureUserExists(user.id);

      // Determina la chiave univoca: spotify_id reale oppure "deezer:{id}"
      const uniqueKey = artistData.spotify_id
        || (artistData.deezer_id ? `deezer:${artistData.deezer_id}` : null)
        || (artistData.id ? `deezer:${artistData.id}` : null);

      if (!uniqueKey) {
        throw new Error('spotify_id or deezer_id is required');
      }

      // Cerca o crea l'artista in artists
      let artistId;

      // Cerca per chiave univoca
      const { data: existingArtist } = await supabase
        .from('artists')
        .select('id')
        .eq('spotify_id', uniqueKey)
        .single();

      if (existingArtist) {
        artistId = existingArtist.id;
      } else {
        // Crea nuovo artista
        const { data: newArtist, error: insertError } = await supabase
          .from('artists')
          .insert({
            spotify_id: uniqueKey,
            name: artistData.name,
            image_url: artistData.image_url || null,
            spotify_url: artistData.spotify_url || artistData.external_urls?.spotify || null,
            genres: artistData.genres || []
          })
          .select('id')
          .single();

        if (insertError) {
          throw insertError;
        }
        artistId = newArtist.id;
      }

      // Aggiungi ai preferiti
      const { error: favoriteError } = await supabase
        .from('artist_favorites')
        .insert({
          user_id: user.id,
          artist_id: artistId
        });

      if (favoriteError) {
        // Se è già nei preferiti, ignora
        if (favoriteError.code === '23505') {
          return { success: true, alreadyFavorite: true };
        }
        throw favoriteError;
      }

      return { success: true, artistId };
    } catch (error) {
      console.error('[Favorites] Error adding favorite:', error);
      throw error;
    }
  }

  /**
   * Rimuove un artista dai preferiti
   */
  async removeFavorite(artistId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('artist_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('artist_id', artistId);

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('[Favorites] Error removing favorite:', error);
      throw error;
    }
  }

  /**
   * Toggle preferito (aggiunge se non c'è, rimuove se c'è)
   */
  async toggleFavorite(artistData) {
    try {
      // Determina la chiave univoca (stessa logica di addFavorite)
      const uniqueKey = artistData.spotify_id
        || (artistData.deezer_id ? `deezer:${artistData.deezer_id}` : null)
        || (artistData.id ? `deezer:${artistData.id}` : null);

      if (!uniqueKey) {
        throw new Error('spotify_id or deezer_id is required');
      }

      // Prima trova l'artist_id
      let artistId;
      const { data: artist } = await supabase
        .from('artists')
        .select('id')
        .eq('spotify_id', uniqueKey)
        .single();

      if (!artist) {
        return await this.addFavorite(artistData);
      }
      artistId = artist.id;

      // Verifica se è già preferito
      const isFav = await this.isFavorite(artistId);
      
      if (isFav) {
        return await this.removeFavorite(artistId);
      } else {
        return await this.addFavorite(artistData);
      }
    } catch (error) {
      console.error('[Favorites] Error toggling favorite:', error);
      throw error;
    }
  }

  /**
   * Assicura che l'utente esista nella tabella users
   */
  async ensureUserExists(userId) {
    try {
      const { error } = await supabase
        .from('users')
        .insert({ user_id: userId })
        .select()
        .single();

      // Se errore è "already exists", va bene
      if (error && error.code !== '23505') {
        // Se è un altro errore, potrebbe essere che esiste già (ignora)
        if (error.code !== 'PGRST116') {
          console.warn('[Favorites] User might already exist:', error.message);
        }
      }
    } catch (error) {
      // Ignora errori di duplicato
    }
  }
}

export default new FavoritesService();

